// ============================================================
// Schedule Store — 排班 + 員工 + 模板 狀態管理
// ============================================================

import { create } from 'zustand';
import { api } from '../lib/api';
import { optimisticCreate, optimisticDelete } from './schedulerPatch';

export interface Staff {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  payType: string;
  hourlyRate?: number;
  monthlySalary?: number;
  preferredPositions: string[];
  tags: string[];
  employmentType: string;
  hireDate: string;
}

export interface ShiftTemplate {
  id: string;
  name: string;
  kind?: 'WORK' | 'REST_DAY' | 'REGULAR_LEAVE';
  startTime: string;
  endTime: string;
  breakStart?: string;
  breakEnd?: string;
  color: string;
}

export interface ScheduleEntry {
  id: string;
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  breakStart?: string;
  breakEnd?: string;
  templateId?: string;
  template?: { id: string; name: string; color: string; kind?: 'WORK' | 'REST_DAY' | 'REGULAR_LEAVE' };
  user?: { id: string; name: string; avatar?: string; preferredPositions?: string };
  position?: string;
  isPublished: boolean;
  note?: string;
}

export interface CostEstimate {
  weekStart: string;
  weekEnd: string;
  totalRegularPay: number;
  totalOvertimePay: number;
  totalLaborCost: number;
  dailyBreakdown: {
    date: string;
    staffCount: number;
    totalHours: number;
    estimatedCost: number;
    belowMinStaff: boolean;
  }[];
  alerts: { type: string; message: string; severity: string }[];
}

/**
 * 切換週次時的「請求序號」。
 *
 * fetchWeekSchedules / fetchWeekInfo / validateAndEstimate 都是非同步且互不等待，
 * 快速連按上下週時，前一週較慢的回應可能在新週之後才抵達，把新週的
 * 發布狀態與人事成本蓋成上一週的（實際看到：空白週卻顯示「已發布」）。
 * 每次切週遞增此序號，回應抵達時序號不符就丟棄。
 */
let requestSeq = 0;

function getMondayOfWeek(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface WeekInfo {
  holidays: Record<string, string>;       // dateStr -> holiday name
  dailyRevenue: Record<string, number>;   // dateStr -> revenue amount
  publishStatus: 'EMPTY' | 'DRAFT' | 'PUBLISHED';
  totalSchedules: number;
  unpublishedCount: number;
}

interface ScheduleState {
  staff: Staff[];
  templates: ShiftTemplate[];
  schedules: ScheduleEntry[];
  weekStart: Date;
  costEstimate: CostEstimate | null;
  weekInfo: WeekInfo | null;
  isLoading: boolean;
  error: string | null;

  setWeekStart: (date: Date) => void;
  navigateWeek: (delta: number) => void;
  fetchStaffAndTemplates: () => Promise<void>;
  fetchWeekSchedules: () => Promise<void>;
  fetchWeekInfo: () => Promise<void>;
  createSchedule: (data: {
    userId: string;
    date: string;
    startTime: string;
    endTime: string;
    breakStart?: string;
    breakEnd?: string;
    templateId?: string;
    position?: string;
  }) => Promise<void>;
  deleteSchedule: (id: string) => Promise<void>;
  /** 指派某格為某個班別（後端 upsert：同人同日會取代既有） */
  assignCell: (userId: string, dateStr: string, template: ShiftTemplate) => Promise<void>;
  /** 清除某格的排班 */
  clearCell: (scheduleId: string) => Promise<void>;
  /** 把上一週的班表整週複製到本週，回傳複製筆數 */
  copyLastWeek: () => Promise<number>;
  validateAndEstimate: () => Promise<void>;
  publishWeek: () => Promise<number>;
  unpublishWeek: () => Promise<number>;
}

export const useScheduleStore = create<ScheduleState>((set, get) => ({
  staff: [],
  templates: [],
  schedules: [],
  weekStart: getMondayOfWeek(new Date()),
  costEstimate: null,
  weekInfo: null,
  isLoading: false,
  error: null,

  setWeekStart: (date) => {
    requestSeq++;
    // 切週時先清掉上一週的摘要，避免新資料到達前顯示舊週的狀態
    set({ weekStart: getMondayOfWeek(date), weekInfo: null, costEstimate: null });
    get().fetchWeekSchedules();
  },

  navigateWeek: (delta) => {
    requestSeq++;
    const prev = get().weekStart;
    const next = new Date(prev);
    next.setDate(next.getDate() + 7 * delta);
    set({ weekStart: next, weekInfo: null, costEstimate: null });
    get().fetchWeekSchedules();
  },

  fetchStaffAndTemplates: async () => {
    try {
      const [staffRes, templateRes] = await Promise.all([
        api.get<{ staff: Staff[] }>('/staff?active=true'),
        api.get<{ templates: ShiftTemplate[] }>('/staff/templates'),
      ]);
      set({ staff: staffRes.staff, templates: templateRes.templates });
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  fetchWeekSchedules: async () => {
    const seq = requestSeq;
    set({ isLoading: true, error: null });
    try {
      const ws = toDateStr(get().weekStart);
      const data = await api.get<{ schedules: ScheduleEntry[] }>(
        `/schedules?weekStart=${ws}`
      );
      if (seq !== requestSeq) return;   // 已切到別週，這份是舊資料
      set({ schedules: data.schedules, isLoading: false });
      // 背景驗證成本 + 取得週資訊
      get().validateAndEstimate();
      get().fetchWeekInfo();
    } catch (err) {
      if (seq !== requestSeq) return;
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchWeekInfo: async () => {
    const seq = requestSeq;
    try {
      const ws = toDateStr(get().weekStart);
      const data = await api.get<WeekInfo>(`/schedules/week-info?weekStart=${ws}`);
      if (seq !== requestSeq) return;
      set({ weekInfo: data });
    } catch { /* non-critical */ }
  },

  createSchedule: async (data) => {
    try {
      await optimisticCreate(set as any, get as any, data);
      get().validateAndEstimate();
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  deleteSchedule: async (id) => {
    try {
      await optimisticDelete(set as any, get as any, id);
      get().validateAndEstimate();
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  // 指派班別。後端 POST /schedules 會 upsert（同 user+date 先刪再建），
  // 所以不論該格原本有沒有班、是不是別的班別，直接送出即可。
  assignCell: async (userId, dateStr, template) => {
    const data: {
      userId: string; date: string; startTime: string; endTime: string;
      templateId?: string; breakStart?: string; breakEnd?: string;
    } = {
      userId,
      date: dateStr,
      startTime: template.startTime,
      endTime: template.endTime,
      templateId: template.id,
    };
    if (template.breakStart) data.breakStart = template.breakStart;
    if (template.breakEnd) data.breakEnd = template.breakEnd;
    await get().createSchedule(data);
  },

  clearCell: async (scheduleId) => {
    await get().deleteSchedule(scheduleId);
  },

  copyLastWeek: async () => {
    const ws = toDateStr(get().weekStart);
    const data = await api.post<{ copied: number }>(
      '/schedules/copy-from-previous-week',
      { weekStart: ws }
    );
    await get().fetchWeekSchedules();
    get().fetchWeekInfo();
    return data.copied;
  },

  validateAndEstimate: async () => {
    const seq = requestSeq;
    try {
      const ws = toDateStr(get().weekStart);
      const data = await api.post<CostEstimate>('/schedules/validate', {
        weekStart: ws,
      });
      if (seq !== requestSeq) return;
      set({ costEstimate: data });
    } catch {
      // 非關鍵，靜默
    }
  },

  publishWeek: async () => {
    const ws = toDateStr(get().weekStart);
    const data = await api.post<{ published: number }>('/schedules/publish', {
      weekStart: ws,
    });
    get().fetchWeekSchedules();
    get().fetchWeekInfo();
    return data.published;
  },

  unpublishWeek: async () => {
    const ws = toDateStr(get().weekStart);
    const data = await api.post<{ unpublished: number }>('/schedules/unpublish', {
      weekStart: ws,
    });
    get().fetchWeekSchedules();
    get().fetchWeekInfo();
    return data.unpublished;
  },
}));
